import { useEffect, useState } from "react";

import {
    Box,
    VStack,
    Text,
    Input,
    Button,
    Flex,
    Image,
    InputGroup,
    useMediaQuery,
} from "@chakra-ui/react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    loginAction,
} from "../../../../state/actions/authActions";

import {
    fetchMyDriverPartnerAction,
} from "../../../../state/actions/driverPartnerActions";

import ActivityToast from
    "../../../../components/ActivityToast";

import {
    toaster,
} from "../../../../components/toaster";

import {
    Eye,
    EyeOff,
    Download,
    X,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import Logo from "../../../../assets/logo.png";
import routes from "../../../../routes";


const LoginForm = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isMobile] = useMediaQuery(
        "(max-width: 768px)"
    );

    const {
        loading,
        error,
    } = useSelector(
        (state) => state.auth
    );


    const [
        username,
        setUsername,
    ] = useState("");

    const [
        password,
        setPassword,
    ] = useState("");

    const [
        showPassword,
        setShowPassword,
    ] = useState(false);


    /*
     * PWA INSTALL
     */
    const [
        deferredPrompt,
        setDeferredPrompt,
    ] = useState(null);

    const [
        showInstallPrompt,
        setShowInstallPrompt,
    ] = useState(false);

    const [
        isInstalled,
        setIsInstalled,
    ] = useState(false);


    const isFormValid =
        username.trim() !== "" &&
        password.trim() !== "";


    /*
     * CHECK IF APP IS ALREADY INSTALLED
     */
    useEffect(() => {
        const standalone =
            window.matchMedia(
                "(display-mode: standalone)"
            ).matches;

        const iosStandalone =
            window.navigator.standalone === true;

        setIsInstalled(
            standalone ||
            iosStandalone
        );
    }, []);


    /*
     * PWA INSTALL PROMPT
     */
    useEffect(() => {
        const handleBeforeInstallPrompt = (
            event
        ) => {
            // Prevent the browser's default prompt
            event.preventDefault();

            // Don't show if already installed
            if (isInstalled) {
                return;
            }

            // Save the install event
            setDeferredPrompt(event);

            // Show our custom notification
            setShowInstallPrompt(true);
        };


        const handleAppInstalled = () => {
            setShowInstallPrompt(false);
            setDeferredPrompt(null);
            setIsInstalled(true);
        };


        window.addEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt
        );

        window.addEventListener(
            "appinstalled",
            handleAppInstalled
        );


        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                handleBeforeInstallPrompt
            );

            window.removeEventListener(
                "appinstalled",
                handleAppInstalled
            );
        };
    }, [isInstalled]);


    /*
     * INSTALL APP
     */
    const handleInstallApp = async () => {
        if (!deferredPrompt) {
            return;
        }

        deferredPrompt.prompt();

        const {
            outcome,
        } = await deferredPrompt.userChoice;


        if (outcome === "accepted") {
            setShowInstallPrompt(false);
        }

        setDeferredPrompt(null);
    };


    /*
     * DISMISS INSTALL NOTIFICATION
     */
    const handleDismissInstall = () => {
        setShowInstallPrompt(false);
    };


    /*
     * LOGIN ERROR
     */
    useEffect(() => {
        if (!error) return;


        if (
            error ===
            "Account is inactive. Please activate your account to log in."
        ) {
            navigate(
                routes.activateAccount,
                {
                    state: {
                        email: username,
                    },
                }
            );

            return;
        }


        toaster.create({
            render: () => (
                <ActivityToast
                    type="error"
                    title="Login Failed"
                    description={error}
                    timestamp="Just now"
                />
            ),
        });
    }, [
        error,
        navigate,
        username,
    ]);


    /*
     * LOGIN
     */
    const handleLogin = async () => {
        const response = await dispatch(
            loginAction(
                username,
                password
            )
        );


        if (response?.success) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Login Successful"
                        description="Welcome back to Mogoma Partner Portal."
                        timestamp="Just now"
                    />
                ),
            });


            await dispatch(
                fetchMyDriverPartnerAction()
            );


            setTimeout(() => {
                navigate(
                    routes.dashboard
                );
            }, 1000);
        }
    };


    return (
        <Box
            p={{
                base: 6,
                md: 10,
                lg: 20,
            }}
        >

            {/* HEADER */}

            <Box
                textAlign="center"
                mb={10}
            >
                <Image
                    src={Logo}
                    h="52px"
                    mx="auto"
                    mb={12}
                />


                <Text
                    fontSize={{
                        base: "36px",
                        md: "48px",
                    }}
                    fontWeight="700"
                    color="#071D54"
                    mb={3}
                    lineHeight="1.1"
                >
                    Welcome Back
                </Text>


                <Text
                    color="gray.600"
                    fontSize="md"
                    maxW="420px"
                    mx="auto"
                >
                    Sign in to manage your Partner business.
                </Text>
            </Box>


            {/* LOGIN FORM */}

            <VStack gap={5}>

                <Input
                    size="lg"
                    placeholder="Email address"
                    value={username}
                    onChange={(e) =>
                        setUsername(
                            e.target.value
                        )
                    }
                />


                <InputGroup
                    endElement={
                        showPassword ? (
                            <EyeOff
                                size={18}
                                cursor="pointer"
                                onClick={() =>
                                    setShowPassword(
                                        false
                                    )
                                }
                            />
                        ) : (
                            <Eye
                                size={18}
                                cursor="pointer"
                                onClick={() =>
                                    setShowPassword(
                                        true
                                    )
                                }
                            />
                        )
                    }
                >
                    <Input
                        size="lg"
                        placeholder="Password"
                        type={
                            showPassword
                                ? "text"
                                : "password"
                        }
                        value={password}
                        onChange={(e) =>
                            setPassword(
                                e.target.value
                            )
                        }
                        onKeyDown={(e) => {
                            if (
                                e.key === "Enter" &&
                                isFormValid
                            ) {
                                handleLogin();
                            }
                        }}
                    />
                </InputGroup>


                <Flex
                    justify="space-between"
                    align="center"
                    w="100%"
                >

                    <Text
                        color="#2453FF"
                        fontWeight="500"
                        cursor="pointer"
                        _hover={{
                            textDecoration:
                                "underline",
                        }}
                        onClick={() =>
                            navigate(
                                routes.activateAccount
                            )
                        }
                    >
                        Activate Account
                    </Text>


                    <Text
                        color="#2453FF"
                        fontWeight="500"
                        cursor="pointer"
                        _hover={{
                            textDecoration:
                                "underline",
                        }}
                        onClick={() =>
                            navigate(
                                routes.forgotPassword
                            )
                        }
                    >
                        Forgot Password?
                    </Text>

                </Flex>


                <Button
                    w="100%"
                    size="lg"
                    bg="#2453FF"
                    color="white"
                    _hover={{
                        bg: "#1E46D8",
                    }}
                    loading={loading}
                    loadingText="Signing In..."
                    disabled={!isFormValid}
                    opacity={
                        !isFormValid
                            ? 0.6
                            : 1
                    }
                    cursor={
                        !isFormValid
                            ? "not-allowed"
                            : "pointer"
                    }
                    onClick={handleLogin}
                >
                    Sign In
                </Button>

            </VStack>


            {/* REGISTER */}

            <Text
                mt={12}
                textAlign="center"
                color="gray.500"
                fontSize="sm"
            >
                Don't have a Partner account?{" "}

                <Text
                    as="span"
                    color="#2453FF"
                    fontWeight="600"
                    cursor="pointer"
                    _hover={{
                        textDecoration:
                            "underline",
                    }}
                    onClick={() =>
                        navigate(
                            routes.register
                        )
                    }
                >
                    Apply to Become a Partner
                </Text>
            </Text>


            {/* PWA INSTALL NOTIFICATION */}

            {showInstallPrompt &&
                isMobile &&
                !isInstalled && (
                    <Box
                        position="fixed"
                        bottom={4}
                        left={4}
                        right={4}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="20px"
                        boxShadow="0 10px 35px rgba(0, 0, 0, 0.15)"
                        p={4}
                        zIndex={9999}
                    >
                        <Flex
                            align="center"
                            gap={3}
                        >

                            {/* ICON */}

                            <Flex
                                w="44px"
                                h="44px"
                                minW="44px"
                                align="center"
                                justify="center"
                                bg="#F1F2FF"
                                color="#2453FF"
                                borderRadius="14px"
                            >
                                <Download
                                    size={21}
                                />
                            </Flex>


                            {/* TEXT */}

                            <Box
                                flex="1"
                                minW="0"
                            >
                                <Text
                                    fontWeight="700"
                                    color="#071D54"
                                    fontSize="sm"
                                >
                                    Install Mogoma
                                </Text>

                                <Text
                                    color="gray.600"
                                    fontSize="xs"
                                    mt={1}
                                >
                                    Install Mogoma for a faster experience.
                                </Text>
                            </Box>


                            {/* INSTALL */}

                            <Button
                                size="sm"
                                bg="#2453FF"
                                color="white"
                                onClick={
                                    handleInstallApp
                                }
                                _hover={{
                                    bg: "#1E46D8",
                                }}
                            >
                                Install
                            </Button>


                            {/* CLOSE */}

                            <Button
                                variant="ghost"
                                size="sm"
                                p={1}
                                minW="auto"
                                onClick={
                                    handleDismissInstall
                                }
                            >
                                <X
                                    size={17}
                                />
                            </Button>

                        </Flex>
                    </Box>
                )}

        </Box>
    );
};


export default LoginForm;